import React from 'react';
import {
  Users,
  Wrench,
  ShieldCheck,
  Briefcase,
  DollarSign,
  TrendingUp,
  MessageSquare,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowRight,
  ShieldAlert,
  Sparkles,
  Zap,
} from 'lucide-react';
import type { AdminDashboardMetrics } from '../../types';

interface AdminOverviewSectionProps {
  metrics: AdminDashboardMetrics | null;
  onNavigateTab: (tab: any) => void;
  pendingPros: any[];
  disputes: any[];
  recentJobs: any[];
}

export const AdminOverviewSection: React.FC<AdminOverviewSectionProps> = ({
  metrics,
  onNavigateTab,
  pendingPros,
  disputes,
  recentJobs,
}) => {
  const m = metrics || {
    totalCustomers: 0,
    totalProfessionals: 0,
    verifiedProfessionals: 0,
    pendingVerifications: 0,
    activeSubscriptions: 0,
    expiredSubscriptions: 0,
    totalJobs: 0,
    openJobs: 0,
    matchingJobs: 0,
    quotedJobs: 0,
    completedJobs: 0,
    cancelledJobs: 0,
    totalSuccessfulPayments: 0,
    subscriptionRevenueNaira: 0,
    whatsappConversations: 0,
    whatsappJobsCreated: 0,
    openDisputes: 0,
  };

  const verificationRate =
    m.totalProfessionals > 0
      ? Math.round((m.verifiedProfessionals / m.totalProfessionals) * 100)
      : 0;

  const subscriptionRate =
    m.totalProfessionals > 0
      ? Math.round((m.activeSubscriptions / m.totalProfessionals) * 100)
      : 0;

  const jobCompletionRate =
    m.totalJobs > 0 ? Math.round((m.completedJobs / m.totalJobs) * 100) : 0;

  return (
    <div id="admin-overview-section" className="space-y-8 animate-fade-in">
      {/* Top 4 Core Business KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Revenue */}
        <div className="bg-slate-900 border border-slate-800/80 rounded-2xl p-5 shadow-sm hover:border-emerald-500/40 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Marketplace Commissions
            </span>
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center font-bold">
              ₦
            </div>
          </div>
          <div className="mt-3">
            <div className="text-2xl font-black text-white font-mono">
              ₦{m.subscriptionRevenueNaira.toLocaleString()}
            </div>
            <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
              <span className="text-emerald-400 font-semibold flex items-center gap-0.5">
                <TrendingUp className="w-3.5 h-3.5" />
                {m.totalSuccessfulPayments} Completed Jobs
              </span>
              <span>•</span>
              <span>10% Platform Commission</span>
            </div>
          </div>
        </div>

        {/* Total Artisans & Verification Rate */}
        <div className="bg-slate-900 border border-slate-800/80 rounded-2xl p-5 shadow-sm hover:border-emerald-500/40 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Artisans & Pros
            </span>
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
              <Wrench className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-2xl font-black text-white flex items-baseline gap-2">
              <span>{m.totalProfessionals}</span>
              <span className="text-xs font-normal text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                {m.verifiedProfessionals} Verified
              </span>
            </div>
            <div className="mt-1 flex items-center justify-between text-xs text-slate-400">
              <span>Verification rate</span>
              <span className="font-semibold text-slate-200">{verificationRate}%</span>
            </div>
          </div>
        </div>

        {/* Active Marketplace Pros */}
        <div className="bg-slate-900 border border-slate-800/80 rounded-2xl p-5 shadow-sm hover:border-emerald-500/40 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Marketplace Coverage
            </span>
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-2xl font-black text-white flex items-baseline gap-2">
              <span>{m.totalProfessionals}</span>
              <span className="text-xs font-normal text-slate-400">
                Registered Pros
              </span>
            </div>
            <div className="mt-1 flex items-center justify-between text-xs text-slate-400">
              <span>Free quoting access</span>
              <span className="font-semibold text-emerald-400">100% Active</span>
            </div>
          </div>
        </div>

        {/* Total Jobs */}
        <div className="bg-slate-900 border border-slate-800/80 rounded-2xl p-5 shadow-sm hover:border-emerald-500/40 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Jobs Matched & Posted
            </span>
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
              <Briefcase className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-2xl font-black text-white flex items-baseline gap-2">
              <span>{m.totalJobs}</span>
              <span className="text-xs font-normal text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                {m.completedJobs} Completed
              </span>
            </div>
            <div className="mt-1 flex items-center justify-between text-xs text-slate-400">
              <span>Fulfillment rate</span>
              <span className="font-semibold text-slate-200">{jobCompletionRate}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary KPIs Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div
          onClick={() => onNavigateTab('customers')}
          className="bg-slate-900/60 border border-slate-800/60 rounded-xl p-3.5 hover:bg-slate-800/80 cursor-pointer transition-all"
        >
          <div className="text-xs text-slate-400">Total Customers</div>
          <div className="text-lg font-bold text-white mt-1">{m.totalCustomers}</div>
        </div>
        <div
          onClick={() => onNavigateTab('whatsapp')}
          className="bg-slate-900/60 border border-slate-800/60 rounded-xl p-3.5 hover:bg-slate-800/80 cursor-pointer transition-all"
        >
          <div className="text-xs text-slate-400">WhatsApp Sessions</div>
          <div className="text-lg font-bold text-emerald-400 mt-1 flex items-center gap-1.5">
            <MessageSquare className="w-4 h-4" />
            <span>{m.whatsappConversations}</span>
          </div>
        </div>
        <div
          onClick={() => onNavigateTab('ai_monitoring')}
          className="bg-slate-900/60 border border-slate-800/60 rounded-xl p-3.5 hover:bg-slate-800/80 cursor-pointer transition-all"
        >
          <div className="text-xs text-slate-400">AI Job Inferences</div>
          <div className="text-lg font-bold text-emerald-400 mt-1 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4" />
            <span>28 Runs</span>
          </div>
        </div>
        <div
          onClick={() => onNavigateTab('disputes')}
          className="bg-slate-900/60 border border-slate-800/60 rounded-xl p-3.5 hover:bg-slate-800/80 cursor-pointer transition-all"
        >
          <div className="text-xs text-slate-400">Active Disputes</div>
          <div className={`text-lg font-bold mt-1 flex items-center gap-1.5 ${m.openDisputes > 0 ? 'text-rose-400' : 'text-slate-300'}`}>
            <AlertTriangle className="w-4 h-4" />
            <span>{m.openDisputes}</span>
          </div>
        </div>
      </div>

      {/* Main Operations Split: Action Queues vs Job Pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Cols: Triage Action Required Queues */}
        <div className="lg:col-span-2 space-y-6">
          {/* Pending Verifications Widget */}
          <div className="bg-slate-900 border border-slate-800/80 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-400" />
                <h2 className="text-sm font-bold text-white">
                  Pending Artisan ID Verifications ({pendingPros.length})
                </h2>
              </div>
              <button
                onClick={() => onNavigateTab('verifications')}
                className="text-xs text-emerald-400 hover:text-emerald-300 font-semibold flex items-center gap-1"
              >
                <span>View All</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {pendingPros.length === 0 ? (
              <div className="py-8 text-center bg-slate-950/40 rounded-xl border border-slate-800/50">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2 opacity-80" />
                <p className="text-xs font-semibold text-slate-300">All verification queues clear!</p>
                <p className="text-[11px] text-slate-500 mt-0.5">No pending artisan submissions awaiting government ID audit.</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {pendingPros.slice(0, 3).map((pro: any) => (
                  <div
                    key={pro.id}
                    className="p-3.5 bg-slate-800/50 border border-slate-700/60 rounded-xl flex items-center justify-between gap-4 hover:border-slate-600 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-amber-500/20 text-amber-300 flex items-center justify-center font-bold text-xs flex-shrink-0">
                        {pro.full_name?.charAt(0) || 'P'}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-white truncate">
                            {pro.full_name}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.2 rounded bg-slate-700 text-slate-300 font-medium">
                            {pro.primary_category || 'Artisan'}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-400 flex items-center gap-2 mt-0.5">
                          <span>{pro.business_name || 'Business'}</span>
                          <span>•</span>
                          <span>{pro.state || 'Lagos'}</span>
                          <span>•</span>
                          <span className="font-mono text-amber-400/90 font-medium">NIN Submission</span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => onNavigateTab('verifications')}
                      className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-slate-950 text-xs font-bold rounded-lg transition-colors flex-shrink-0"
                    >
                      Audit ID
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Active Disputes Widget */}
          <div className="bg-slate-900 border border-slate-800/80 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-rose-400" />
                <h2 className="text-sm font-bold text-white">
                  Active Disputes & Support Cases ({disputes.length})
                </h2>
              </div>
              <button
                onClick={() => onNavigateTab('disputes')}
                className="text-xs text-emerald-400 hover:text-emerald-300 font-semibold flex items-center gap-1"
              >
                <span>Dispute Desk</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {disputes.length === 0 ? (
              <div className="py-8 text-center bg-slate-950/40 rounded-xl border border-slate-800/50">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2 opacity-80" />
                <p className="text-xs font-semibold text-slate-300">Zero active disputes</p>
                <p className="text-[11px] text-slate-500 mt-0.5">All customer and artisan job transactions running harmoniously.</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {disputes.slice(0, 2).map((disp: any) => (
                  <div
                    key={disp.id}
                    className="p-3.5 bg-slate-800/50 border border-rose-500/20 rounded-xl space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-rose-300 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                        {disp.reason || 'Case In Progress'}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded font-bold uppercase bg-rose-500/20 text-rose-300">
                        {disp.status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-300 line-clamp-2">{disp.description}</p>
                    <div className="pt-1 flex items-center justify-between text-[11px] text-slate-400 border-t border-slate-700/50">
                      <span>Job: <strong className="text-slate-200">{disp.jobTitle || disp.jobId}</strong></span>
                      <button
                        onClick={() => onNavigateTab('disputes')}
                        className="text-emerald-400 hover:text-emerald-300 font-semibold"
                      >
                        Resolve Case
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right 1 Col: Job Pipeline & Quick Controls */}
        <div className="space-y-6">
          {/* Real-time Job Pipeline Breakdown */}
          <div className="bg-slate-900 border border-slate-800/80 rounded-2xl p-5 space-y-4">
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-emerald-400" />
              <span>Job Pipeline Breakdown</span>
            </h2>

            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-400">Open for Matching</span>
                  <span className="font-bold text-emerald-400">{m.openJobs}</span>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-2">
                  <div
                    className="bg-emerald-500 h-2 rounded-full"
                    style={{ width: `${Math.min(100, (m.openJobs / Math.max(m.totalJobs, 1)) * 100)}%` }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-400">Quotes Submitted</span>
                  <span className="font-bold text-blue-400">{m.quotedJobs}</span>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${Math.min(100, (m.quotedJobs / Math.max(m.totalJobs, 1)) * 100)}%` }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-400">Assigned / In Progress</span>
                  <span className="font-bold text-amber-400">{m.assignedJobs || 0}</span>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-2">
                  <div
                    className="bg-amber-500 h-2 rounded-full"
                    style={{ width: `${Math.min(100, ((m.assignedJobs || 0) / Math.max(m.totalJobs, 1)) * 100)}%` }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-400">Successfully Completed</span>
                  <span className="font-bold text-purple-400">{m.completedJobs}</span>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full"
                    style={{ width: `${Math.min(100, (m.completedJobs / Math.max(m.totalJobs, 1)) * 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="pt-3 border-t border-slate-800">
              <button
                onClick={() => onNavigateTab('jobs')}
                className="w-full py-2 bg-slate-800 hover:bg-slate-750 text-slate-200 text-xs font-semibold rounded-xl border border-slate-700/60 transition-colors flex items-center justify-center gap-1.5"
              >
                <span>Manage All Jobs</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Broadcast Tool Shortcut */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-950 border border-emerald-500/30 rounded-2xl p-5 space-y-3">
            <div className="flex items-center gap-2 text-emerald-400">
              <Zap className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-wider">Fast Broadcast</span>
            </div>
            <p className="text-xs text-slate-300">
              Send emergency system maintenance alerts or promotional notices to all registered artisans & customers in real time.
            </p>
            <button
              onClick={() => onNavigateTab('broadcast')}
              className="w-full py-2 bg-emerald-500 hover:bg-emerald-600 text-slate-950 text-xs font-bold rounded-xl transition-colors shadow-md"
            >
              Compose Broadcast Alert
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
